import { isSumsubFinalAnswer } from "@repo/shared";
import type {
  ComponentValueResponse,
  IdentityMismatchField,
  ResolveIdentityConflictRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import * as sumsubVerificationApi from "@/lib/api/flow-instances/sumsub-verification";
import { sumsubValueFingerprint } from "./sumsub-value-fingerprint";
import { useInvalidateFlowCompletion } from "./use-flow-completion";

export const sumsubVerificationKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "sumsub-verification", "flow-instance-value"] as const,
  identityConflicts: (id: string | null, valueFingerprint: string, subjectId?: string) =>
    [
      "flowInstances",
      id,
      "sumsub-verification",
      "identity-conflict",
      valueFingerprint,
      subjectId ?? null,
    ] as const,
};

export function useSumsubVerificationFlowInstanceValue(id: string | null) {
  const invalidateCompletion = useInvalidateFlowCompletion();
  const queryClient = useQueryClient();
  // null = no observation yet (don't invalidate on mount); boolean = last observed terminality.
  const lastSeenTerminalRef = useRef<boolean | null>(null);

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: sumsubVerificationKeys.flowInstanceValue(id),
    queryFn: () => sumsubVerificationApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });

  // Webhook fallback: pull the latest applicant state from Sumsub, persist it,
  // and seed the cache with the result. Used by the manual "refetch" so a
  // verification resolves even when the `applicantReviewed` webhook never landed.
  const syncFromSumsub = useCallback(async (): Promise<ComponentValueResponse | null> => {
    if (!id) return null;
    const fresh = await sumsubVerificationApi.refetchFromSumsub(id);
    queryClient.setQueryData(sumsubVerificationKeys.flowInstanceValue(id), fresh);
    return fresh;
  }, [id, queryClient]);

  // When the value transitions to a terminal reviewAnswer (GREEN/RED), the engine's
  // sumsub_verification predicate flips to complete. The save mutation already invalidates
  // the completion cache; this covers the webhook-driven polling path where the backend
  // writes the snapshot and the frontend only learns about it via refetch.
  useEffect(() => {
    if (!id || !data) return;
    let isTerminal = false;
    if (data.calculatedData) {
      try {
        isTerminal = isSumsubFinalAnswer(JSON.parse(data.calculatedData)?.status?.reviewAnswer);
      } catch {
        // malformed blob — treat as non-terminal
      }
    }
    if (lastSeenTerminalRef.current === null) {
      // First observation — establish baseline without invalidating.
      lastSeenTerminalRef.current = isTerminal;
      return;
    }
    if (isTerminal && !lastSeenTerminalRef.current) {
      lastSeenTerminalRef.current = true;
      invalidateCompletion(id);
    } else if (!isTerminal && lastSeenTerminalRef.current) {
      // Fresh attempt — reset so the next terminal triggers another invalidate.
      lastSeenTerminalRef.current = false;
    }
  }, [data, id, invalidateCompletion]);

  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
    refetch,
    syncFromSumsub,
  };
}

/**
 * QL-f — identity conflicts on the advisor page.
 *
 * Mirrors `usePublicIdentityConflicts`, including the completion invalidation:
 * answering the last conflict is what makes the component complete, so the
 * Submit gate has to be re-read afterwards.
 */
export function useIdentityConflicts(
  id: string | null,
  calculatedData?: string | null,
  /** QL-654 — whose conflicts, when several people are verified. */
  subjectId?: string,
) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();

  // The saved result is written by five different paths — the widget save, the
  // webhook, the manual refetch, "use previous answer" and the reuse apply.
  // Folding its fingerprint into the KEY means every one of them refreshes the
  // conflicts automatically, instead of five call sites each remembering to
  // invalidate and one of them eventually not.
  const fingerprint = sumsubValueFingerprint(calculatedData);
  const key = sumsubVerificationKeys.identityConflicts(id, fingerprint, subjectId);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => sumsubVerificationApi.getIdentityConflicts(id!, subjectId),
    enabled: !!id,
  });

  const resolve = useMutation({
    mutationFn: (payload: ResolveIdentityConflictRequest) =>
      sumsubVerificationApi.resolveIdentityConflict(id!, { ...payload, subjectId }),
    onSuccess: (result) => {
      queryClient.setQueryData(key, result);
      // An "update" answer edits the client, so anything showing the client's
      // name, date of birth or Verified ID badge is now stale.
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      if (id) invalidateCompletion(id);
    },
  });

  return {
    rows: data?.rows ?? [],
    unresolved: data?.unresolved ?? [],
    isLoading,
    resolve: async (field: IdentityMismatchField, decision: "update" | "dismiss") => {
      await resolve.mutateAsync({ field, decision });
    },
  };
}
